import { useEffect, useState, useSyncExternalStore } from 'react';
import { T } from 'gt-react';
import { isAppViewSpec } from '@erp/json-renderer/apps';
import { AppView, createAppStateStore } from '@erp/json-renderer/apps/react';
import type { AppStateStore } from '@erp/json-renderer/apps/react';
import type { WorkbookFeedStatus, OpenedReport } from '@erp/rpc';
import { useReportFeeds } from '@/api/rpc/hooks/apps/useApps';
import { useDebounce } from '@/hooks/useDebounce';
import {
  failedReadPatch,
  feedsPatch,
  inputsOrEmpty,
  inputsPatch,
  loadingPatch,
  readInputs,
} from './reportState';

/** Filter changes settle this long before the feeds are read again. */
const INPUT_DEBOUNCE_MS = 300;

/**
 * Keeps the view's feeds in step with its filters: once the state the feeds
 * read has settled on new values, reads them again and patches the store,
 * and carries the filters into the URL and this browser.
 */
function useFeedsFollowInputs(
  store: AppStateStore,
  opened: OpenedReport,
  onInputsChange?: (inputs: Record<string, unknown>) => void,
) {
  const inputsJson = useSyncExternalStore(store.subscribe, () =>
    JSON.stringify(readInputs(store.get, opened.inputs)),
  );
  const [openedJson] = useState(() => inputsJson);
  const settledJson = useDebounce(inputsJson, INPUT_DEBOUNCE_MS);
  // Once the filters moved, every settled value is read (going back to the
  // opening filters too); until then the open's own read stands.
  const [changed, setChanged] = useState(false);
  if (!changed && settledJson !== openedJson) setChanged(true);
  const read = useReportFeeds({
    appId: opened.app.id,
    deploymentId: opened.deployment.id,
    inputs: inputsOrEmpty(JSON.parse(settledJson)),
    enabled: changed,
  });
  const feeds = read.data?.feeds ?? opened.feeds;

  useEffect(() => {
    if (read.isFetching) store.update(loadingPatch(feeds));
  }, [read.isFetching, store, feeds]);

  useEffect(() => {
    if (!read.data) return;
    store.update(feedsPatch(read.data.feeds));
  }, [read.data, store]);

  // A reopen (newer Results) lands here until the filters move; after that the reads do.
  useEffect(() => {
    if (!changed) store.update(feedsPatch(opened.feeds));
  }, [changed, opened.feeds, store]);

  const failure = read.isFetching ? null : (read.error?.message ?? null);
  useEffect(() => {
    if (failure !== null) store.update(failedReadPatch(feeds, failure));
  }, [failure, store, feeds]);

  useEffect(() => {
    if (!changed) return;
    // The page writes settled filters to its URL and this browser, not to React state.
    // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent
    onInputsChange?.(inputsOrEmpty(JSON.parse(settledJson)));
  }, [changed, settledJson, onInputsChange]);

  return { feeds, readError: read.error };
}

/**
 * A Report's view over its feeds, read as the viewer: on its own page, or in
 * a Workbook's App Output. Filters are view state; changing them reads the
 * feeds again and patches the view in place.
 */
export function ReportView({
  opened,
  initialInputs,
  onInputsChange,
}: {
  opened: OpenedReport;
  initialInputs: Record<string, unknown>;
  /** The filters, once they changed and settled. */
  onInputsChange?: (inputs: Record<string, unknown>) => void;
}) {
  const [store] = useState(() => {
    const created = createAppStateStore({ ...opened.view.state });
    created.update({
      ...inputsPatch(initialInputs, opened.inputs),
      ...feedsPatch(opened.feeds),
    });
    return created;
  });
  const { feeds, readError } = useFeedsFollowInputs(
    store,
    opened,
    onInputsChange,
  );

  return (
    <>
      {readError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {readError.message}
        </div>
      ) : null}
      <FeedErrors feeds={feeds} />
      {isAppViewSpec(opened.view) ? (
        <AppView spec={opened.view} store={store} />
      ) : null}
    </>
  );
}

function FeedErrors({
  feeds,
}: {
  feeds: Readonly<Record<string, WorkbookFeedStatus>>;
}) {
  const failed = Object.entries(feeds).flatMap(([key, feed]) =>
    feed.status === 'error' ? [{ key, message: feed.message }] : [],
  );
  if (failed.length === 0) return null;
  return (
    <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
      <T context="Report page: some of the Report's data failed to load">
        Some of this Report's data could not be loaded:
      </T>
      <ul className="mt-1 list-disc pl-5">
        {failed.map((error) => (
          <li key={error.key}>
            <span className="font-mono">{error.key}</span>: {error.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
